import { Group } from '@visx/group';
import { scaleBand } from '@visx/scale';
import { BarGroup } from '@visx/shape';
import { chunk, keyBy } from 'lodash';

import { aggregators } from '../../../../../utils';

import { BrushPreviewGroupProps } from './brush-preview.model';

const useOptimizeBarGroupPreview = <T extends {}>({
    data,
    xMax,
    group,
    xAccessor,
}: Pick<BrushPreviewGroupProps<T>, 'group' | 'xAccessor' | 'data' | 'xMax'>) => {
    const groupKeys = group.series.map((series: any) => series.column);
    const columns = keyBy(group.series, 'column');
    const getColor = (key: string) => columns?.[key]?.color ?? '#eee';

    const groupSize = groupKeys.length;
    const minBandwidth = xMax / (groupSize + 1.5);

    const requiredChunkSize = Math.ceil(data.length / minBandwidth);

    const groupChunkedData = chunk(data, requiredChunkSize)
        .flatMap(chunk => {
            const [datum = {}] = chunk;
            const aggregateDatum = { ...(datum as any) };
            groupKeys.forEach((column: any) => {
                aggregateDatum[column] = aggregators.avg(chunk, d => d[column]) ?? 0;
            });
            return aggregateDatum as T[];
        })
        .filter(Boolean);

    const x0Scale = scaleBand({
        range: [0, xMax],
        padding: 0.2,
        domain: groupChunkedData.map(xAccessor),
        reverse: true,
    });

    const x1Scale = scaleBand<string>({
        domain: groupKeys,
    }).rangeRound([0, x0Scale.bandwidth()]);

    return {
        data: groupChunkedData,
        x0Scale,
        x1Scale,
        getColor,
        keys: groupKeys,
    };
};

const BarGroupPreview = <T extends {}>({
    groupKey,
    data,
    xMax,
    group,
    xAccessor,
    yScale,
    yMax,
}: BrushPreviewGroupProps<T>) => {
    const previewConfig = useOptimizeBarGroupPreview({ data, xMax, group, xAccessor });
    return (
        <BarGroup
            data={previewConfig.data}
            keys={previewConfig.keys}
            height={yMax}
            x0={xAccessor}
            x0Scale={previewConfig.x0Scale}
            x1Scale={previewConfig.x1Scale}
            yScale={yScale}
            color={previewConfig.getColor}
        >
            {barGroups =>
                barGroups.map(barGroup => {
                    return (
                        <Group key={`${groupKey}-bar-group-${barGroup.index}-${barGroup.x0}`} left={barGroup.x0}>
                            {barGroup.bars.map(bar => (
                                <rect
                                    key={`${groupKey}-bar-group-bar-${barGroup.index}-${bar.index}-${bar.value}-${bar.key}`}
                                    x={bar.x}
                                    y={bar.y}
                                    width={bar.width}
                                    height={bar.height}
                                    fill={bar.color}
                                    opacity={0.65}
                                />
                            ))}
                        </Group>
                    );
                })
            }
        </BarGroup>
    );
};

export { BarGroupPreview };
